import { defaultBusinessPersonData } from '@/context/services/tpm/hooks';
import { BusinessPersonData, TPMData } from '@/context/services/tpm/type';
import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

type BusinessPersonDataContextType = {
  businessPersonData: BusinessPersonData;
  setBusinessPersonData: (value: BusinessPersonData) => void;
};

const businessPersonDataContext = createContext(defaultBusinessPersonData);
const setBusinessPersonDataContext = createContext<
  React.Dispatch<React.SetStateAction<BusinessPersonData>>
>(() => {});

type Props = {
  businessPersonData?: BusinessPersonData;
};

export const BusinessPersonDataProvider: FC<PropsWithChildren<Props>> = ({
  children,
  businessPersonData: initData,
}) => {
  const [businessPersonData, setBusinessPersonData] =
    useState<BusinessPersonData>(initData ?? defaultBusinessPersonData);

  return (
    <setBusinessPersonDataContext.Provider value={setBusinessPersonData}>
      <businessPersonDataContext.Provider value={businessPersonData}>
        {children}
      </businessPersonDataContext.Provider>
    </setBusinessPersonDataContext.Provider>
  );
};

export const useBusinessPersonData =
  (): BusinessPersonDataContextType['businessPersonData'] => {
    const data = useContext(businessPersonDataContext);
    if (data === undefined) {
      throw new Error(
        'useBusinessPersonData must be used within a BusinessPersonDataProvider'
      );
    }
    return data;
  };

export const useSetBusinessPersonData =
  (): BusinessPersonDataContextType['setBusinessPersonData'] => {
    const setter = useContext(setBusinessPersonDataContext);
    if (setter === undefined) {
      throw new Error(
        'useBusinessPersonData must be used within a BusinessPersonDataProvider'
      );
    }
    return useCallback(
      (value: BusinessPersonData) => {
        setter((prev) => {
          if (
            prev.address !== value.address ||
            prev.companyType !== value.companyType ||
            prev.disabled !== value.disabled ||
            prev.name !== value.name ||
            JSON.stringify(prev.term || '{}') !==
              JSON.stringify(value.term || '{}')
          ) {
            return value;
          }
          return prev;
        });
      },
      [setter]
    );
  };

export const useTpmDataFromBusinessPersonData = (): TPMData => {
  const businessPersonData = useBusinessPersonData();
  const [personData, setPersonData] = useState(
    businessPersonData.parseValidityDate()
  );
  useEffect(() => {
    const data = businessPersonData.parseValidityDate();
    setPersonData((prev) => {
      if (isNotEqualTPM(prev, data)) {
        return data;
      }
      return prev;
    });
  }, [businessPersonData]);

  return personData;
};

export const isNotEqualTPM = (v1: TPMData, v2: TPMData) => {
  return (
    v1.validityEndDate !== v2.validityEndDate ||
    v1.validityStartDate !== v2.validityStartDate
  );
};
